import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserCheck, UserX, Trash2, Users, CheckCircle2, XCircle, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS_FILTERS = ["Pending", "Approved", "Rejected", "All"];

const STATUS_STYLES = {
  Pending: { icon: Clock, cls: "bg-[#d4af37]/10 text-[#8a6d1d] border-[#d4af37]/30" },
  Approved: { icon: CheckCircle2, cls: "bg-[#234333]/10 text-[#234333] border-[#234333]/30" },
  Rejected: { icon: XCircle, cls: "bg-[#9e2a2b]/10 text-[#9e2a2b] border-[#9e2a2b]/30" },
};

export default function AlumniRegistrations() {
  const { role } = useAuth();
  const writable = role === "institute_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Pending");
  const [busyId, setBusyId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmReject, setConfirmReject] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institute/alumni-registrations");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(
    () => (filter === "All" ? items : items.filter((x) => x.status === filter)),
    [items, filter],
  );
  const counts = useMemo(() => {
    const c = { Pending: 0, Approved: 0, Rejected: 0, All: items.length };
    items.forEach((x) => (c[x.status] = (c[x.status] || 0) + 1));
    return c;
  }, [items]);

  const approve = async (reg) => {
    setBusyId(reg.id);
    try {
      const { data } = await api.post(`/institute/alumni-registrations/${reg.id}/approve`);
      setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      toast.success("Approved — alumni added to the public roster");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Approval failed");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    if (!confirmReject) return;
    setBusyId(confirmReject.id);
    try {
      const { data } = await api.post(`/institute/alumni-registrations/${confirmReject.id}/reject`);
      setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      toast.success("Marked as rejected");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Rejection failed");
    } finally {
      setBusyId(null);
      setConfirmReject(null);
    }
  };

  const del = async () => {
    if (!confirmDelete) return;
    setBusyId(confirmDelete.id);
    try {
      await api.delete(`/institute/alumni-registrations/${confirmDelete.id}`);
      setItems((arr) => arr.filter((x) => x.id !== confirmDelete.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    } finally {
      setBusyId(null);
      setConfirmDelete(null);
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="alumni-reg-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Engagement</div>
        <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Alumni Registrations</h1>
        <p className="mt-2 text-sm text-[#6b655b]">
          Review submissions from your public Alumni Registration form. Approving a submission
          publishes the alumnus to your live public Alumni roster.
        </p>
      </div>

      <div className="mb-5 flex flex-wrap gap-2">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`ar-filter-${s}`}
            className={`px-3 py-1.5 text-xs tracking-wide uppercase border rounded-sm flex items-center gap-2 ${
              filter === s
                ? "bg-[#234333] text-white border-[#234333]"
                : "bg-white text-[#6b655b] border-[#e5e0d8] hover:bg-[#efece6]"
            }`}
          >
            {s} <span className="opacity-70">({counts[s] || 0})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div data-testid="ar-empty" className="border border-dashed border-[#e5e0d8] rounded-sm p-12 text-center bg-white">
          <Users size={28} className="mx-auto text-[#6b655b] mb-3" />
          <p className="text-sm text-[#6b655b]">No registrations in this view.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f9f8f6]">
              <tr>
                {["", "Name", "Passing Year", "Branch", "Profession", "Mobile", "Email", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-wide uppercase text-[#6b655b] font-semibold text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#efece6]">
              {filtered.map((it) => {
                const StatusIcon = STATUS_STYLES[it.status]?.icon || Clock;
                const full = [it.first_name, it.middle_name, it.last_name].filter(Boolean).join(" ");
                return (
                  <tr key={it.id} data-testid={`ar-row-${it.id}`} className="hover:bg-[#f9f8f6]/60 cursor-pointer" onClick={() => setViewing(it)}>
                    <td className="px-4 py-3">
                      {it.photo_url ? (
                        <img src={it.photo_url} alt={full} className="w-10 h-10 object-cover rounded-full border border-[#e5e0d8]" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#efece6]" />
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#1c1a17] font-medium">{full}</td>
                    <td className="px-4 py-3 text-[#6b655b]">{it.passing_year}</td>
                    <td className="px-4 py-3 text-[#6b655b]">{it.branch || "—"}</td>
                    <td className="px-4 py-3 text-[#6b655b]">{it.current_profession || "—"}</td>
                    <td className="px-4 py-3 text-[#6b655b]">{it.mobile_no}</td>
                    <td className="px-4 py-3 text-[#6b655b]">{it.email || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${STATUS_STYLES[it.status]?.cls || ""}`}>
                        <StatusIcon size={10} /> {it.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {writable ? (
                        <div className="flex gap-1 justify-end">
                          {it.status === "Pending" ? (
                            <>
                              <button
                                onClick={() => approve(it)}
                                disabled={busyId === it.id}
                                data-testid={`ar-approve-${it.id}`}
                                className="inline-flex items-center gap-1 p-2 text-[#234333] hover:bg-[#234333]/5 rounded-sm disabled:opacity-50"
                              >
                                <UserCheck size={14} />
                              </button>
                              <button
                                onClick={() => setConfirmReject(it)}
                                disabled={busyId === it.id}
                                data-testid={`ar-reject-${it.id}`}
                                className="p-2 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 rounded-sm disabled:opacity-50"
                              >
                                <UserX size={14} />
                              </button>
                            </>
                          ) : null}
                          <button
                            onClick={() => setConfirmDelete(it)}
                            disabled={busyId === it.id}
                            data-testid={`ar-delete-${it.id}`}
                            className="p-2 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 rounded-sm disabled:opacity-50"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewing ? (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          onClick={() => setViewing(null)}
          data-testid="ar-view-modal"
        >
          <div
            className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b">
              <div className="flex items-center gap-4">
                {viewing.photo_url ? (
                  <img src={viewing.photo_url} alt="" className="w-20 h-20 object-cover rounded border" />
                ) : (
                  <div className="w-20 h-20 rounded bg-[#efece6]" />
                )}
                <div>
                  <div className="font-serif text-2xl">
                    {[viewing.first_name, viewing.middle_name, viewing.last_name].filter(Boolean).join(" ")}
                  </div>
                  <div className="text-sm text-[#6b655b]">
                    {viewing.passing_year} · {viewing.branch || "—"} · {viewing.current_profession || "—"}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {[
                ["Gender", viewing.gender],
                ["Marital Status", viewing.marital_status],
                ["Date of Birth", viewing.date_of_birth],
                ["Mobile", viewing.mobile_no],
                ["Email", viewing.email],
                ["LinkedIn", viewing.linkedin_url],
                ["Country", viewing.country],
                ["State", viewing.state],
                ["City", viewing.city],
                ["Zipcode", viewing.zipcode],
                ["Submitted", viewing.created_at?.slice(0, 10)],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <span className="text-[10px] tracking-wider uppercase text-[#6b655b] w-28 shrink-0 mt-0.5">{k}</span>
                  <span className="text-[#1c1a17] break-words">{v || "—"}</span>
                </div>
              ))}
              <div className="sm:col-span-2 flex gap-2">
                <span className="text-[10px] tracking-wider uppercase text-[#6b655b] w-28 shrink-0 mt-0.5">Address</span>
                <span className="text-[#1c1a17] break-words">{viewing.current_address || "—"}</span>
              </div>
            </div>
            <div className="p-4 border-t text-right">
              <button onClick={() => setViewing(null)} className="text-sm px-4 py-2 hover:bg-[#efece6] rounded-sm">Close</button>
            </div>
          </div>
        </div>
      ) : null}

      <AlertDialog open={!!confirmReject} onOpenChange={(o) => !o && setConfirmReject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this registration?</AlertDialogTitle>
            <AlertDialogDescription>
              The applicant won&apos;t be published. You can still delete or revisit this record later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={reject} className="bg-[#9e2a2b] hover:bg-[#7e2122] text-white">Reject</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this registration?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently removes the entry. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-[#9e2a2b] hover:bg-[#7e2122] text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
